import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { dealTeamRoleLabel, type DealTeamRole } from "@shared/crm-team";
import { UserPlus, ArrowRightLeft, X, Users, History, Crown } from "lucide-react";
import TransferDealDialog, {
  DealTeamRoleBadge,
  RoleSelect,
} from "@/components/Crm/TransferDealDialog";

function iniciais(nome: string | null | undefined) {
  if (!nome) return "?";
  return nome.split(" ").map((p) => p[0]).join("").toUpperCase().slice(0, 2);
}

function dataCurta(v: string | Date | null | undefined) {
  if (!v) return "";
  return new Date(v).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

/**
 * Time interno da negociação: quem trabalha e em que papel.
 *
 * Existe porque `ownerUserId` guarda UMA pessoa, e o trabalho real é dividido —
 * a SDR qualifica e marca a reunião, a closer conduz o fechamento. Sem este
 * painel a transferência sobrescrevia o responsável e o crédito de quem
 * originou a oportunidade sumia.
 */
export default function DealTeamPanel({ dealId }: { dealId: number }) {
  const { can } = usePermissions();
  const utils = trpc.useUtils();
  const podeGerenciar = can("crm:deals_assign") || can("crm:deals_assign_all");

  const [addOpen, setAddOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  const teamQuery = trpc.crm.deals.listTeam.useQuery({ dealId });
  const transfersQuery = trpc.crm.deals.listTransfers.useQuery({ dealId });
  const dealQuery = trpc.crm.deals.get.useQuery({ id: dealId });

  const invalidate = () => {
    utils.crm.deals.listTeam.invalidate({ dealId });
    utils.crm.deals.listTransfers.invalidate({ dealId });
    utils.crm.deals.listActivities.invalidate({ dealId });
    utils.crm.deals.get.invalidate({ id: dealId });
  };

  const remove = trpc.crm.deals.removeTeamMember.useMutation({
    onSuccess: () => {
      toast.success("Removido do time");
      invalidate();
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao remover")),
  });

  const team = teamQuery.data ?? [];
  const transfers = transfersQuery.data ?? [];
  const ownerUserId = dealQuery.data?.deal?.ownerUserId ?? null;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4" />
            Time da negociação
            {team.length > 0 && <Badge variant="secondary">{team.length}</Badge>}
          </CardTitle>
          {podeGerenciar && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}>
                <UserPlus className="h-3.5 w-3.5 sm:mr-1.5" />
                <span className="hidden sm:inline">Adicionar</span>
              </Button>
              <Button size="sm" onClick={() => setTransferOpen(true)}>
                <ArrowRightLeft className="h-3.5 w-3.5 sm:mr-1.5" />
                <span className="hidden sm:inline">Transferir</span>
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {teamQuery.isLoading && (
            <p className="text-sm text-muted-foreground">Carregando…</p>
          )}
          {!teamQuery.isLoading && team.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Ninguém marcado ainda. Registre quem qualificou (SDR) e quem vai
              fechar (Closer) — é o que permite creditar as duas pontas depois.
            </p>
          )}
          {team.map((row: any) => (
            <div
              key={row.member.id}
              className="flex items-start gap-3 rounded-lg border border-border p-3"
            >
              <div className="h-9 w-9 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                {iniciais(row.user?.name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-sm font-medium truncate">
                    {row.user?.name ?? row.user?.email ?? "Usuário removido"}
                  </span>
                  <DealTeamRoleBadge role={row.member.role} />
                  {row.member.userId === ownerUserId && (
                    <Badge variant="outline" className="text-[11px] gap-1">
                      <Crown className="h-3 w-3" />
                      Responsável
                    </Badge>
                  )}
                </div>
                {row.user?.jobTitle && (
                  <p className="text-xs text-muted-foreground truncate">{row.user.jobTitle}</p>
                )}
                {row.member.notes && (
                  <p className="text-xs text-muted-foreground mt-1">{row.member.notes}</p>
                )}
                <p className="text-[11px] text-muted-foreground/70 mt-1">
                  Desde {dataCurta(row.member.createdAt)}
                </p>
              </div>
              {podeGerenciar && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                  title="Remover do time"
                  aria-label="Remover do time"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate({ id: row.member.id })}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {transfers.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <History className="h-4 w-4" />
              Passagens de bastão
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {transfers.map((row: any) => (
              <div key={row.transfer.id} className="text-sm border-l-2 border-border pl-3 py-1">
                <p>
                  <span className="font-medium">{row.fromName ?? "Sem responsável"}</span>
                  {row.transfer.fromRole && (
                    <span className="text-muted-foreground"> ({dealTeamRoleLabel(row.transfer.fromRole)})</span>
                  )}
                  <ArrowRightLeft className="inline h-3 w-3 mx-1.5 text-muted-foreground" />
                  <span className="font-medium">{row.toName ?? "—"}</span>
                  {row.transfer.toRole && (
                    <span className="text-muted-foreground"> ({dealTeamRoleLabel(row.transfer.toRole)})</span>
                  )}
                </p>
                {row.transfer.reason && (
                  <p className="text-xs text-muted-foreground mt-0.5">{row.transfer.reason}</p>
                )}
                <p className="text-[11px] text-muted-foreground/70 mt-0.5">
                  {new Date(row.transfer.createdAt).toLocaleString("pt-BR", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <AddMemberSheet
        dealId={dealId}
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onDone={invalidate}
      />
      <TransferDealDialog
        dealId={dealId}
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        onDone={invalidate}
      />
    </div>
  );
}

// ─── Adicionar alguém ao time ────────────────────────────────────────────────

function AddMemberSheet({
  dealId,
  open,
  onClose,
  onDone,
}: {
  dealId: number;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState("");
  const [notes, setNotes] = useState("");

  const usersQuery = trpc.crm.deals.listAssignableUsers.useQuery(undefined, { enabled: open });

  const add = trpc.crm.deals.addTeamMember.useMutation({
    onSuccess: (row) => {
      // null = a pessoa já estava no time com esse papel (o insert é
      // idempotente). Dizer "adicionado" seria mentira boba.
      toast.success(row ? "Adicionado ao time" : "Essa pessoa já estava no time com esse papel");
      setUserId("");
      setRole("");
      setNotes("");
      onDone();
      onClose();
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao adicionar ao time")),
  });

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Adicionar ao time</SheetTitle>
          <SheetDescription>
            Alguém da casa que trabalha esta negociação. Não muda o responsável.
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Pessoa</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Escolha quem entra" />
              </SelectTrigger>
              <SelectContent>
                {(usersQuery.data ?? []).map((u: any) => (
                  <SelectItem key={u.id} value={String(u.id)}>
                    {u.name ?? u.email}
                    {u.jobTitle ? ` — ${u.jobTitle}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Papel</Label>
            <div className="mt-1">
              <RoleSelect value={role} onChange={setRole} />
            </div>
          </div>
          <div>
            <Label>Observação (opcional)</Label>
            <Textarea
              className="mt-1"
              rows={3}
              placeholder="Ex: entrou depois da reunião técnica"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          <Button
            className="w-full"
            disabled={!userId || !role || add.isPending}
            onClick={() =>
              add.mutate({
                dealId,
                userId: Number(userId),
                role: role as DealTeamRole,
                notes: notes.trim() || null,
              })
            }
          >
            {add.isPending ? "Salvando…" : "Adicionar"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
